import cx from 'classnames'
import {
  type ReactNode,
  type FormEventHandler,
  useMemo,
  useId,
  useCallback,
} from 'react'
import type { FormControlProps } from '@/infra/prop-types'
import styles from './Form.module.css'
import { AutoSkeleton, useIsSkeleton } from '@/components/Skeleton'
import flattenChildren from 'react-keyed-flatten-children'

export interface FormProps {
  children?: ReactNode

  /**
   * ID of the form.
   */
  id?: string

  /**
   * Form submit handler.
   */
  onSubmit?: FormEventHandler

  /**
   * Reduces the gaps between fields.
   */
  compact?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * The Form itself.
 */
export function Form({ id, onSubmit, compact, testId, children }: FormProps) {
  const submit: FormEventHandler = useCallback(
    (e) => {
      if (onSubmit) {
        e.preventDefault()
        onSubmit(e)
      }
    },
    [onSubmit],
  )

  return (
    <form
      noValidate
      id={id}
      action="#" // makes sure Safari shows a `go` button on the keyboard rather than `return`
      onSubmit={submit}
      data-test={testId}
      className={cx(styles.form, compact && styles.compact)}
    >
      {children}
    </form>
  )
}

interface FormFieldSetProps {
  children?: ReactNode
}

/**
 * Form Field Set
 */
export function FormFieldSet({ children }: FormFieldSetProps) {
  return <fieldset className={styles.fieldSet}>{children}</fieldset>
}

interface FormFieldSetLegendProps {
  children?: ReactNode
  id?: string
  isSkeleton?: boolean
}

/**
 * Form Field Set Legend
 */
export function FormFieldSetLegend({
  isSkeleton,
  children,
  id,
}: FormFieldSetLegendProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <legend id={id} className={styles.fieldSetLegend}>
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </legend>
  )
}

interface FormFieldSetDescriptionProps {
  children?: ReactNode
  isSkeleton?: boolean
}

/**
 * Form Field Set Description
 */
export function FormFieldSetDescription({
  children,
  isSkeleton,
}: FormFieldSetDescriptionProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <div className={styles.fieldSetDescription}>
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </div>
  )
}

interface FormFieldGroupProps {
  children?: ReactNode

  /**
   * Whether to stack elements on compact layouts.
   */
  stackWhenCompact?: boolean
}

/**
 * Group form fields, so they appear together (side by side).
 */
export function FormFieldGroup({
  children,
  stackWhenCompact,
}: FormFieldGroupProps) {
  return (
    <div
      className={cx(
        styles.fieldGroup,
        stackWhenCompact && styles.stackWhenCompact,
      )}
    >
      {children}
    </div>
  )
}

interface FieldProps {
  children?: ReactNode
}

/**
 * A field in the form.
 */
export function FormField({ children }: FieldProps) {
  return <div className={styles.field}>{children}</div>
}

interface FormFieldLabelProps {
  children?: ReactNode

  /**
   * ID of the label.
   */
  id?: string

  /**
   * Which input (`name`) is the label for.
   */
  for?: string

  /**
   * Adds styling indicating that the field is invalid.
   */
  invalid?: boolean

  /**
   * Whether to render a skeleton instead of the label.
   */
  isSkeleton?: boolean
}

/**
 * A label for a form field.
 */
export function FormFieldLabel({
  children,
  invalid,
  isSkeleton,
  id,
  for: htmlFor,
}: FormFieldLabelProps) {
  return (
    <label
      id={id}
      htmlFor={htmlFor}
      className={cx(styles.label, invalid && styles.labelInvalid)}
    >
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </label>
  )
}

interface FormFieldHintProps {
  children?: ReactNode
  id?: string

  /**
   * Whether the hint should indicate invalidity.
   */
  invalid?: boolean

  /**
   * Whether to render a skeleton instead of the hint.
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Form Field Hint
 */
export function FormFieldHint({
  children,
  id,
  invalid,
  isSkeleton,
  testId,
}: FormFieldHintProps) {
  // We don't want the hint element to take up space if there's no content to
  // display.
  const isRendered = flattenChildren(children).length > 0

  return (
    <div
      id={id}
      // Announce changes to content when it changes
      aria-live="polite"
      data-test={testId}
      className={cx(
        styles.fieldHint,
        invalid && styles.fieldHintInvalid,
        !isRendered && styles.fieldHintHidden,
      )}
    >
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </div>
  )
}

/**
 * Provides information about the field (if any) a
 * component is contained within.
 */
interface FormFieldHookResult {
  /**
   * Props for the form field control.
   */
  control: FormControlProps
  /**
   * Props for the form field hint.
   */
  hint: FormFieldHintProps
  /**
   * Props for the form field label.
   */
  label: FormFieldLabelProps
}

export interface UseFormFieldProps {
  name?: string
  invalid?: boolean
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

/**
 * Manages props that are to be distributed on to a form
 * field label, control, and hint.
 */
export function useFormField({
  name,
  invalid,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: UseFormFieldProps = {}): FormFieldHookResult {
  // ID of the field. Used by the control to link to the accessible description
  // if available.
  const fieldId = useId()
  // ID of the label. Used by the control to link to the accessible label.
  const fallbackLabelId = useId()
  const labelId = ariaLabelledBy || fallbackLabelId
  const fallbackHintId = `${name ?? fieldId}-hint`
  const hintId = ariaDescribedBy || fallbackHintId

  // Props to pass to controls.
  const controlProps = useMemo((): FormControlProps => {
    const isInvalid = Boolean(invalid)
    return {
      name: name,
      id: fieldId,
      invalid: isInvalid,
      'aria-describedby': hintId,
      // Only add the `labelledby` if no explicit label was passed.
      'aria-labelledby': ariaLabel ? undefined : labelId,
      'aria-label': ariaLabel,
    }
  }, [invalid, name, fieldId, hintId, ariaLabel, labelId])

  // Props to pass to the hint.
  const hintProps = useMemo(
    (): FormFieldHintProps => ({
      id: hintId,
      invalid: Boolean(invalid),
    }),
    [hintId, invalid],
  )

  // Props for the label.
  const labelProps = useMemo(
    (): FormFieldLabelProps => ({
      for: fieldId,
      invalid: Boolean(invalid),
      id: labelId,
    }),
    [fieldId, invalid, labelId],
  )

  // The form field context wires up the controls and the labels + hints
  // to be accessible and propagates validation state.
  return useMemo(
    (): FormFieldHookResult => ({
      control: controlProps,
      hint: hintProps,
      label: labelProps,
    }),
    [controlProps, hintProps, labelProps],
  )
}
